import { supabase } from "@/lib/supabase-client";
import { CourseType } from "@/types/db/courses";
import { FromDB } from "@/types";
import { FileMetadata } from "@/types/files";

export const courseService = {
  async getCourses(): Promise<FromDB<CourseType>[]> {
    const { data, error } = await supabase
      .from("courses")
      .select("id, name, status, description, open_enrollment, created_at");
    if (error) throw new Error(error.message);
    return data as FromDB<CourseType>[];
  },

  async getCourse(id: string): Promise<CourseType> {
    const { data, error } = await supabase.from("courses").select("*").eq("id", id).single();
    if (error) throw new Error(error.message);
    return data as CourseType;
  },

  async updateCourse(id: string, updates: Partial<CourseType>): Promise<CourseType> {
    const { data, error } = await supabase.from("courses").update(updates).eq("id", id).select().single();
    if (error) throw new Error(error.message);
    return data as CourseType;
  },

  async saveRowPatches(patches: Partial<CourseType> & { id: string }[]): Promise<void> {
    await Promise.all(
      patches.map(async (patch) => {
        const { error } = await supabase.from("courses").update(patch).eq("id", patch.id);
        if (error) throw new Error(error.message);
      }),
    );
  },

  async createCourse(courseData: {
    name: string;
    description?: string;
    status?: string;
    open_enrollment?: boolean;
    meta_data?: CourseType["meta_data"];
  }): Promise<CourseType> {
    const { data, error } = await supabase
      .from("courses")
      .insert({
        name: courseData.name,
        description: courseData.description,
        status: courseData.status || "DRAFT",
        open_enrollment: courseData.open_enrollment || false,
        meta_data: courseData.meta_data || {
          settings: {},
          sections: [],
          grading: { method: "percentage", passingScore: 70 },
        },
      })
      .select()
      .single();
    if (error) throw new Error(error.message);
    return data as CourseType;
  },

  async deleteCoursesByIds(ids: string[]): Promise<void> {
    const { error } = await supabase.from("courses").delete().in("id", ids);
    if (error) throw new Error(error.message);
  },

  // File management methods
  async uploadFile(courseId: string, file: File, uploadedBy: string): Promise<FileMetadata> {
    // Get tenant_id from course record
    const { data: tenantData, error: tenantError } = await supabase
      .from("courses")
      .select("tenant_id")
      .eq("id", courseId)
      .single();

    if (tenantError || !tenantData) {
      throw new Error(`Failed to get course tenant: ${tenantError?.message || "Course not found"}`);
    }

    const fileId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const filePath = `${tenantData.tenant_id}/${courseId}/${fileId}-${file.name}`;

    // Upload file to storage
    const { error: uploadError } = await supabase.storage.from("courses").upload(filePath, file);

    if (uploadError) {
      throw new Error(`Upload failed: ${uploadError.message}`);
    }

    // Create file metadata (store path, not URL)
    const fileMetadata: FileMetadata = {
      id: fileId,
      name: file.name,
      size: file.size,
      type: file.type,
      url: filePath, // Store path for signed URL generation
      bucket: "courses", // Store bucket name
      uploadedAt: new Date().toISOString(),
      uploadedBy: uploadedBy,
    };

    return fileMetadata;
  },

  async getFiles(courseId: string): Promise<FileMetadata[]> {
    // Get tenant_id from course record
    const { data: tenantData, error: tenantError } = await supabase
      .from("courses")
      .select("tenant_id")
      .eq("id", courseId)
      .single();

    if (tenantError || !tenantData) {
      throw new Error(`Failed to get course tenant: ${tenantError?.message || "Course not found"}`);
    }

    // List files from storage bucket
    const storagePath = `${tenantData.tenant_id}/${courseId}`;
    const { data: files, error: listError } = await supabase.storage.from("courses").list(storagePath);

    if (listError) {
      throw new Error(`Failed to list course files: ${listError.message}`);
    }

    if (!files || files.length === 0) {
      return [];
    }

    // Convert storage files to FileMetadata
    const fileMetadata: FileMetadata[] = files.map((file) => {
      // Parse fileId from filename (format: {fileId}-{filename})
      const dashIndex = file.name.indexOf("-");
      const fileId = dashIndex > 0 ? file.name.substring(0, dashIndex) : file.name;
      const fileName = dashIndex > 0 ? file.name.substring(dashIndex + 1) : file.name;

      return {
        id: fileId,
        name: fileName,
        size: file.metadata?.size || 0,
        type: file.metadata?.mimetype || "application/octet-stream",
        url: `${storagePath}/${file.name}`, // Store path for signed URL generation
        bucket: "courses", // Store bucket name
        uploadedAt: file.created_at || new Date().toISOString(),
        uploadedBy: "unknown",
      };
    });

    return fileMetadata;
  },

  async deleteFile(courseId: string, fileId: string): Promise<boolean> {
    // Get tenant_id from course record
    const { data: tenantData, error: tenantError } = await supabase
      .from("courses")
      .select("tenant_id")
      .eq("id", courseId)
      .single();

    if (tenantError || !tenantData) {
      throw new Error(`Failed to get course tenant: ${tenantError?.message || "Course not found"}`);
    }

    // List files from storage to find the one to delete
    const storagePath = `${tenantData.tenant_id}/${courseId}`;
    const { data: files, error: listError } = await supabase.storage.from("courses").list(storagePath);

    if (listError) {
      throw new Error(`Failed to list course files: ${listError.message}`);
    }

    // Find the file that starts with the fileId
    const fileToDelete = files?.find((file) => file.name.startsWith(`${fileId}-`));

    if (!fileToDelete) {
      throw new Error("File not found in storage");
    }

    // Delete from storage
    const filePath = `${storagePath}/${fileToDelete.name}`;
    const { error: storageError } = await supabase.storage.from("courses").remove([filePath]);

    if (storageError) {
      throw new Error(`Failed to delete from storage: ${storageError.message}`);
    }

    return true;
  },

  async renameFile(courseId: string, fileId: string, newName: string): Promise<FileMetadata> {
    const { data: tenantData, error: tenantError } = await supabase
      .from("courses")
      .select("tenant_id")
      .eq("id", courseId)
      .single();

    if (tenantError || !tenantData) {
      throw new Error(`Failed to get course tenant: ${tenantError?.message || "Course not found"}`);
    }

    const storagePath = `${tenantData.tenant_id}/${courseId}`;
    const { data: files, error: listError } = await supabase.storage.from("courses").list(storagePath);

    if (listError) {
      throw new Error(`Failed to list course files: ${listError.message}`);
    }

    const fileToRename = files?.find((file) => file.name.startsWith(`${fileId}-`));

    if (!fileToRename) {
      throw new Error("File not found in storage");
    }

    const oldPath = `${storagePath}/${fileToRename.name}`;
    const newPath = `${storagePath}/${fileId}-${newName}`;

    const { error: moveError } = await supabase.storage.from("courses").move(oldPath, newPath);

    if (moveError) {
      throw new Error(`Failed to rename file: ${moveError.message}`);
    }

    return {
      id: fileId,
      name: newName,
      size: fileToRename.metadata?.size || 0,
      type: fileToRename.metadata?.mimetype || "application/octet-stream",
      url: newPath,
      bucket: "courses",
      uploadedAt: fileToRename.created_at || new Date().toISOString(),
      uploadedBy: "unknown",
    };
  },
};
